import React, { useEffect, useState, useRef, useCallback } from "react";
import { TouchableOpacity, View, TextInput, Image, ActivityIndicator } from "react-native";
import { FONT_SIZE, THEME_COLORS, images } from "../themes";
import { commonStyles } from "../styles";
import { testIds } from "../testID";
import { debounce, IconButton } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Text, TooltipV3 } from "../component";
import SearchIcon from "@mui/icons-material/Search";
import { Close } from "@mui/icons-material";

type PaginatorType = {
  numPages: number;
  perPage: number;
  currentPage: number;
  totalItems: number;
};

type onLeftChevronPressType = (prevPage: number) => void;
type onRightChevronPressType = (nextPage: number) => void;
type onReloadPressType = () => void;

export interface IPaginatorViewProps {
  paginator: PaginatorType;
  loading: boolean;
  leftTextOne: string;
  onLeftChevronPress?: onLeftChevronPressType;
  onRightChevronPress?: onRightChevronPressType;
  isSearchVisible?: boolean;
  onSearchInput?: (text: string) => void;
  centerContent?: JSX.Element;
  rightContent?: JSX.Element;
  loaderView?: JSX.Element;
  inputPlaceholderText?: string;
  searchTextValue?: string;
  isReloadVisible?: boolean;
  onReload?: onReloadPressType;
  searchVal?: string;
  error?: boolean;
}

const NavigationView = React.memo((props: {
  paginator: PaginatorType;
  onLeftChevronPress?: onLeftChevronPressType;
  loading: boolean;
  loaderView?: JSX.Element;
  onRightChevronPress?: onRightChevronPressType;
  onReload?: onReloadPressType;
  isReloadVisible?: boolean;
}) =>  {
  if (!!!props.paginator) return <></>;
  const { currentPage, numPages, perPage, totalItems } = props.paginator;
  const { onLeftChevronPress, onRightChevronPress, isReloadVisible, onReload, loading, loaderView } = props;
  const startLimit = totalItems === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === numPages;
  const endLimit = isLastPage ? totalItems : currentPage * perPage;
  const chevronScale = 0.8;
  // const Loader = loaderView ? loaderView : <Text style={{marginHorizontal: 5, fontSize: 20}}>Loading...</Text>;
  return (
    <View>
      {isReloadVisible && (
        <TouchableOpacity style={{ margin: 10, flexDirection: "row" }} onPress={onReload}>
          <Image
            source={images.reloadIcon}
            style={{ width: 20 * chevronScale, height: 20 * chevronScale, tintColor: THEME_COLORS.primaryPurple }}
          />
          {loading ? <Text style={{ marginHorizontal: 5, fontSize: 20 }}>Loading...</Text> : null}
        </TouchableOpacity>
      )}

      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <TouchableOpacity
          disabled={isFirstPage}
          style={{ opacity: isFirstPage ? 0.5 : 1, marginHorizontal: 5 }}
          onPress={() => {
            onLeftChevronPress(currentPage - 1);
          }}
        >
          <Image
            source={require("./../../assets/ic_chevron_left_24_px.png")}
            style={{ width: 15 * chevronScale, height: 27 * chevronScale }}
          />
        </TouchableOpacity>
        <TouchableOpacity
          disabled={isLastPage}
          style={{ opacity: isLastPage ? 0.5 : 1, marginHorizontal: 5 }}
          onPress={() => {
            onRightChevronPress(currentPage + 1);
          }}
        >
          <Image
            source={require("./../../assets/ic_chevron_right_24_px.png")}
            style={{ width: 15 * chevronScale, height: 27 * chevronScale }}
          />
        </TouchableOpacity>
        <Text
          fontSize={"small"}
          style={[
            {
              color: THEME_COLORS.grey0,
            },
          ]}
        >
          {startLimit}-{endLimit} of {totalItems} entries
        </Text>
      </View>
    </View>
  );
});

const PaginatorView = (props: IPaginatorViewProps) => {
  const [searchText, setSearchText] = useState(props.searchVal || "");
  const { loading, onLeftChevronPress, onRightChevronPress, isReloadVisible, onReload, error } = props;
  const placeholderText =
    props.inputPlaceholderText !== "" ? props.inputPlaceholderText : "Search by Part, Technician, Date etc...";
  const [tooltipIsOpen, setIsTooltipOpen] = useState(false);
  const textChangedRef = useRef(false);

  useEffect(() => {
    setSearchText(props.searchTextValue);
  }, [props.searchTextValue]);

  // const onKeyPress = (e) => {
  //   if (!tooltipIsOpen && textChangedRef.current) {
  //     setIsTooltipOpen(true);
  //   }

  //   if (e.key === "Enter" && !loading) {
  //     debouncedSearch(searchText);
  //     if (tooltipIsOpen) {
  //       setIsTooltipOpen(false);
  //     }
  //   }
  // };

  // Optimized key press handler
  const onKeyPress = useCallback((e) => {
    console.log("key press", e.key,new Date().getTime());
    if (e.key === "Enter" && textChangedRef.current) {
      handleSearch(searchText);
      setIsTooltipOpen(false);
    }
  }, [searchText]);

  // Create a memoized search handler that runs on the next tick
  const handleSearch = useCallback((text: string) => {
    if (!props.loading) {
      requestAnimationFrame(() => {
        props.onSearchInput(text);
        textChangedRef.current = false;
      });
    }
  }, [props.loading]);

  // Add debounced search handler
  const debouncedSearch = useRef(
    debounce((text: string) => {
      props.onSearchInput(text);
      textChangedRef.current = false;
    }, 300)
  ).current;

  const handlePaste = useCallback((e) => {
    const pastedText = e.clipboardData.getData('text');
    setSearchText(pastedText);
    textChangedRef.current = true;
  }, []);

  //text change handler
  const handleTextChange = useCallback((text: string) => {
    setSearchText(text);
    textChangedRef.current = true;
  }, []);

  return (
    <View
      style={[
        {
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
          paddingHorizontal: 0,
          marginTop: 10,
          marginBottom: 20,
        },
      ]}
    >
      <View style={[{ flexDirection: "column", minWidth: 200 }]}>
        <View style={[{ flexDirection: "row", alignItems: "center", marginBottom: !!props.leftTextOne ? 5 : 0 }]}>
          <Text style={{ fontSize: FONT_SIZE.tabFontSize, fontWeight: "bold" }}>{props.leftTextOne}</Text>
        </View>
        <NavigationView
          loading={!!loading}
          paginator={props.paginator}
          isReloadVisible={isReloadVisible}
          onReload={onReload}
          onLeftChevronPress={onLeftChevronPress}
          onRightChevronPress={onRightChevronPress}
        />
      </View>
      {props.isSearchVisible ? (
        <View style={{ marginHorizontal: 20 }}>
          <TooltipV3 open={tooltipIsOpen} title={"Press enter to search"}>
            <View style={[commonStyles.input2, { flexDirection: "row", alignItems: "center" }]}>
              {error ? null : (
                <>
                  <TextInput
                    style={[{ paddingLeft: 10, outline: "none" }]}
                    placeholder={placeholderText}
                    value={searchText}
                    autoFocus={true}
                    testID={testIds.listSearchInput}
                    onChangeText={handleTextChange}
                    onKeyPress={onKeyPress}
                    onPaste={handlePaste}
                    maxLength={30} 
                  />
                  {searchText ? (
                    <IconButton
                      style={{ padding: 0 }}
                      onClick={() => {
                      setSearchText('');
                      props.onSearchInput('');
                      textChangedRef.current = false;
                    }}
                  >
                    <Close style={{ fontSize: 24 }} />
                  </IconButton>
                ) : null}
                  {!!loading ? (
                    <ActivityIndicator color={THEME_COLORS.purple} size="small" />
                  ) : (
                    <IconButton
                      style={{ padding: 0 }}
                      disabled={!textChangedRef.current}
                      onClick={() => {
                        if (textChangedRef.current) {
                          props.onSearchInput(searchText);
                          textChangedRef.current = false;
                        }
                      }}
                    >
                      <SearchIcon style={{ fontSize: 24 }} />
                    </IconButton>
                  )}
                </>
              )}
            </View>
          </TooltipV3>
        </View>
      ) : null}
      {props.centerContent}
      <View style={[{ flex: 1 }]}></View>
      {props.rightContent}
    </View>
  );
};

export default React.memo(PaginatorView);
